import { api } from "./client";

export interface TsRow {
  id: string;
  employee_id: string;
  venue_id: string;
  venue_name: string;
  timezone: string;
  work_date: string;
  role: string | null;
  crosses_midnight: boolean;
  status: string;
  payable_approved: boolean;
  missing_out: boolean;
  payable_in: string | null;
  payable_out: string | null;
  payable_in_local: string | null;
  payable_out_local: string | null;
  break_count: number;
  unpaid_break_seconds: number;
  on_break: boolean;
  elapsed_seconds: number | null;
  elapsed_hms: string | null;
  paid_seconds: number | null;
  paid_hms: string | null;
  paid_hours_decimal: string | null;
  chips: string[];
  in_sms: string | null;
  in_gps: string | null;
  out_sms: string | null;
  out_gps: string | null;
  gps_in_passed: boolean | null;
  gps_out_passed: boolean | null;
  revision_count: number;
  open_exceptions: number;
}

export interface TsWeek {
  week_start: string;
  week_end: string;
  rows: TsRow[];
  paid_hms: string;
  paid_hours_decimal: string;
  regular_hours: string;
  daily_ot_hours: string;
  weekly_ot_hours: string;
  weekly_ot_note: string | null;
}

export interface Timesheet {
  groups: { employee_id: string; employee_name: string | null; weeks: TsWeek[] }[];
  banner: { text: string; key: string };
  draft: boolean;
}

export interface BreakEntry {
  id: string;
  start_local: string;
  end_local: string | null;
  start_input: string;
  end_input: string | null;
  hms: string | null;
  type: "MEAL" | "REST" | "UNCLASSIFIED";
  source: "ADMIN" | "SMS";
  classification: "UNPAID_MEAL" | "PAID_SHORT" | "NEEDS_REVIEW" | null;
  admin_classification: "PAID" | "UNPAID" | null;
  status: "ACTIVE" | "VOIDED";
  unpaid: boolean | null;
  flags: string[];
  void_reason?: string;
  note: string | null;
}

export interface Revision {
  revision_no: number;
  source: string;
  reason: string | null;
  actor_name: string;
  created_at: string;
  in_local: string | null;
  out_local: string | null;
}

export interface SessionDetail {
  timezone: string;
  venue_name: string;
  timeline: { type: string; source: string; sms: string | null; gps: string | null; payable: string | null; delay_sec: number | null }[];
  revisions: Revision[];
  breaks: BreakEntry[];
  corrections: { id: string; status: string; requested_values: Record<string, string | null>; review_note: string | null }[];
  exceptions: { id: string; code: string; status: string; type: string }[];
}

export interface Blocking { label: string; count: number; items: { code: string; employee_name?: string | null; session_id?: string | null; message: string }[] }

export interface MyCorrection {
  id: string;
  category: string;
  status: string;
  requested_in_local: string | null;
  requested_out_local: string | null;
  note: string | null;
  messages: { from: "Admin" | "You"; kind: string; text: string; at: string }[];
  decision: { decision: string; reason: string | null; at: string } | null;
}

export interface MyTimesheet {
  periods: { id: string; label: string; is_current: boolean }[];
  period: { id: string; start_date: string; end_date: string } | null;
  rows: { id: string; work_date: string; venue_name: string; role: string | null; payable_in: string | null; payable_out: string | null;
    break_count: number; unpaid_break_seconds: number; paid_hm: string | null; paid_hours_decimal: string | null; status_label: string;
    correction_status: string | null; crosses_midnight: boolean; on_break: boolean }[];
  total_paid_hm?: string;
  total_paid_hours_decimal?: string;
}

export interface CorrectionOptions {
  sessions: { session_id: string; assignment_id: string; label: string; finalized?: boolean }[];
  shifts: { assignment_id: string; label: string; finalized?: boolean }[];
}

const qs = (o: Record<string, string | boolean | undefined>) => {
  const p = new URLSearchParams();
  Object.entries(o).forEach(([k, v]) => { if (v !== undefined && v !== "" && v !== false) p.set(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const tsApi = {
  list: (f: Record<string, string | boolean | undefined>) => api.get<Timesheet>(`/admin/timesheets${qs(f)}`),
  detail: (id: string) => api.get<SessionDetail>(`/admin/timesheets/sessions/${id}`),
  addBreak: (sid: string, body: Record<string, unknown>) => api.post<BreakEntry>(`/admin/timesheets/sessions/${sid}/breaks`, body),
  editBreak: (bid: string, body: Record<string, unknown>) => api.put<BreakEntry>(`/admin/breaks/${bid}`, body),
  voidBreak: (bid: string, reason: string) => api.post<BreakEntry>(`/admin/breaks/${bid}/void`, { reason }),
  classify: (bid: string, classification: string, note: string) => api.post<BreakEntry>(`/admin/breaks/${bid}/classify`, { classification, note }),
  blocking: (pid: string) => api.get<Blocking>(`/admin/pay/periods/${pid}/blocking`),
  mine: (period?: string) => api.get<MyTimesheet>(`/me/timesheets${qs({ period })}`),
  myCorrections: () => api.get<MyCorrection[]>("/me/corrections"),
  options: () => api.get<CorrectionOptions>("/me/correction-options"),
  reply: (correction_id: string, message: string) => api.post<MyCorrection>("/me/corrections/reply", { correction_id, message }),
  withdraw: (correction_id: string) => api.post<MyCorrection>("/me/corrections/withdraw", { correction_id }),
};

export const CHIP_LABEL: Record<string, string> = {
  MISSING_OUT: "No clock-out", PAYABLE_NOT_APPROVED: "Not approved", RATE_MISSING: "Rate missing", ROLE_UNMAPPED: "Role unmapped",
  BREAK_NEEDS_REVIEW: "Break needs review", OVERLAPPING_SESSIONS: "Overlap", DUPLICATE_SESSION: "Duplicate",
  LATE_VERIFICATION: "Late verification", LATE_OUT_CONTEXT: "Late OUT", BREAK_AUTO_ENDED_AT_OUT: "Break auto-ended at OUT",
  BREAK_OPEN_AT_CLOSE: "Break open at close", MISSING_PAYABLE_TIME: "Missing payable time",
};
